import { chain, isArray, sortBy, toString } from 'lodash';

const formatQueryKey = (key: string | string[]) => {
  return (extras?: unknown) => {
    return chain([
      key,
      isArray(extras)
        ? sortBy(extras.map((item) => toString(item)))
        : toString(extras),
    ])
      .flatten()
      .compact()
      .value();
  };
};

const genericQueryKey = (key: string) => ({
  /* Get all resources */
  all: formatQueryKey([key, 'list']),
  pagination: formatQueryKey([key, 'pagination']),
  /* Get single resource */
  details: formatQueryKey([key, 'details']),
  invalidateAll: formatQueryKey(key),
});

export const queryKeys = {
  assemblies: genericQueryKey('assemblies'),
  assemblyListing: genericQueryKey('assemblyListing'),
  assemblyComponents: genericQueryKey('assemblyComponents'),
  assemblyTags: genericQueryKey('assemblyTags'),
  assets: genericQueryKey('assets'),
  ccAssets: genericQueryKey('ccAssets'),
  channels: genericQueryKey('channels'),
  components: genericQueryKey('components'),
  componentListing: genericQueryKey('componentListing'),
  componentAssets: genericQueryKey('componentAssets'),
  componentTypes: genericQueryKey('componentTypes'),
  dayparts: genericQueryKey('dayparts'),
  environment: genericQueryKey('environment'),
  healthCheck: genericQueryKey('healthCheck'),
  layouts: genericQueryKey('layouts'),
  layoutScreens: genericQueryKey('layoutScreens'),
  layoutTypes: genericQueryKey('layoutTypes'),
  layoutTags: genericQueryKey('layoutTags'),
  locationGroups: genericQueryKey('locationGroups'),
  locations: genericQueryKey('locations'),
  locationChannels: genericQueryKey('locationChannels'),
  locationByGroup: genericQueryKey('locationByGroup'),
  locationGroupDayparts: genericQueryKey('locationGroupDayparts'),
  locationGroupAssemblies: genericQueryKey('locationGroupAssemblies'),
  locationDayparts: genericQueryKey('locationDayparts'),
  locationAssemblies: genericQueryKey('locationAssemblies'),
  locationGroupSchedule: genericQueryKey('locationGroupSchedule'),
  locationDataHistory: genericQueryKey('locationDataHistory'),
  locationDataVersion: genericQueryKey('locationDataVersion'),
  locationTags: genericQueryKey('locationTags'),
  componentTags: genericQueryKey('componentTags'),
  locationOwnerTags: genericQueryKey('locationOwnerTags'),
  products: genericQueryKey('products'),
  productCategories: genericQueryKey('productCategories'),
  productVariations: genericQueryKey('productVariations'),
  productVariationTypes: genericQueryKey('productVariationTypes'),
  productVariationFlavors: genericQueryKey('productVariationFlavors'),
  productPublications: genericQueryKey('productPublications'),
  projectConfigs: genericQueryKey('projectConfigs'),
  publications: genericQueryKey('Publications'),
  enrichedLocations: genericQueryKey('enrichedLocations'),
  rbDataHistory: genericQueryKey('rbDataHistory'),
  rbDataVersion: genericQueryKey('rbDataVersion'),
  scheduleOverrides: genericQueryKey('scheduleOverrides'),
  screenSections: genericQueryKey('screenSections'),
  schedules: genericQueryKey('schedules'),
  scheduleOverview: genericQueryKey('scheduleOverview'),
  scheduleList: genericQueryKey('scheduleList'),
  scheduleDayparts: genericQueryKey('scheduleDayparts'),
  scheduleAssemblies: genericQueryKey('scheduleAssemblies'),
  defaultAssemblies: genericQueryKey('defaultAssemblies'),
  scheduleAssembliesSearch: genericQueryKey('scheduleAssembliesSearch'),
  scheduleDaypartAssemblies: genericQueryKey('scheduleDaypartAssemblies'),
  scheduleDaypartOverrides: genericQueryKey('scheduleDaypartOverrides'),
  sections: genericQueryKey('sections'),
  syncLocations: genericQueryKey('syncLocations'),
  themes: genericQueryKey('themes'),
  userActions: genericQueryKey('userActions'),
  viewAssetsWithComponents: genericQueryKey('viewAssetsWithComponents'),
  viewAssembliesWithComponents: genericQueryKey('viewAssembliesWithComponents'),
  viewChannels: genericQueryKey('viewChannels'),
  viewLocations: genericQueryKey('viewLocations'),
  viewLocationLayoutsWithScheduleAssemblies: genericQueryKey(
    'viewLocationLayoutsWithScheduleAssemblies',
  ),
  viewUserActionUsers: genericQueryKey('viewUserActionUsers'),
  weekdayDayparts: genericQueryKey('weekdayDayparts'),
  assemblySectionOverrides: genericQueryKey('assemblySectionOverrides'),
  viewLocationsByGroups: genericQueryKey('viewLocationsByGroups'),
  bulkReplaceEligibleAssemblyIds: genericQueryKey(
    'bulkReplaceEligibleAssemblyIds',
  ),
  viewProductCategoriesWithProducts: genericQueryKey(
    'viewProductCategoriesWithProducts',
  ),
  viewComponentTagWithComponentCount: genericQueryKey(
    'viewComponentTagsWithComponentsCount',
  ),
  viewAssemblyTagsWithAssembliesCount: genericQueryKey(
    'viewAssemblyTagsWithAssembliesCount',
  ),
  bulkReplaceRequests: genericQueryKey('bulkReplaceRequests'),
  bulkReplaceRequestNamesByAssemblyIds: genericQueryKey(
    'bulkReplaceRequestNamesByAssemblyIds',
  ),
  getLocationGroupsWithLocationCount: genericQueryKey(
    'getLocationGroupsWithLocationCount',
  ),
  locationListing: genericQueryKey('locationListing'),
  getLocationSubzones: genericQueryKey('getLocationSubzones'),
  getLocationZones: genericQueryKey('getLocationZones'),
  getLocationTimezones: genericQueryKey('getLocationTimezones'),
  getCalendarSidebarDetails: genericQueryKey('getSidebarDetails'),
  getCalendarEvents: genericQueryKey('getCalendarEvents'),
  useViewLocationsByGroupsWithoutPaginationFilterByLocationCode:
    genericQueryKey(
      'useViewLocationsByGroupsWithoutPaginationFilterByLocationCode',
    ),
  locationActiveDays: genericQueryKey('locationActiveDays'),
  daypartGlobalComponent: genericQueryKey('daypartGlobalComponent'),
  schedulePreview: genericQueryKey('schedulePreview'),
  dyTemplates: genericQueryKey('dyTemplates'),
  canaryRolloutStatus: genericQueryKey('canaryRolloutStatus'),
};
